import { isNull } from "lodash"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import TextField from "@/components/form/textField/TextField"
import { PlayerMetricCellProps } from "@/app/settings/metric/zones/components/type"
import { CustomPlayerMetric } from "@/types/metric"
import {
  checkIsErrCustomThreshold,
  HR_BLOCKED_KEYS,
  isAllowedZoneCellInput,
} from "@/app/settings/metric/zones/components/validation"

export default function PlayerMetricCell({
  individualRanges,
  individualValuesRef,
  playerMetrics,
  setPlayerMetrics,
  params,
  type,
  index,
  min,
  max,
  findRowIndex,
}: PlayerMetricCellProps) {
  const ref = useRef<HTMLDivElement>(null)

  const [isVisible, setIsVisible] = useState(false)

  const isNegative = min < 0 && max <= 0
  const fixedNumber = type === "hr" ? 0 : 1
  const step = type === "hr" ? 1 : 0.1
  const newIndex = type === "hr" ? index + 1 : index
  const rowIndex = findRowIndex(params.row.idfPlayer)

  // 존 임계값 계산 헬퍼 함수
  const calculateZoneValue = useCallback(
    (zoneIndex: number, maxValue: number | string) => {
      return (
        Number(individualValuesRef.current?.[type][zoneIndex]) *
        0.01 *
        Number(maxValue)
      ).toFixed(fixedNumber)
    },
    [individualValuesRef, type, fixedNumber],
  )

  const getPlayerValue = useCallback(
    (max: number | string, idx: number) => {
      const currentZoneEnd = calculateZoneValue(idx, max)
      const isLastZone = index === (individualRanges?.[type].length ?? 0) - 1

      // 첫 번째 존 (hr 제외)
      if (index === 0 && type !== "hr") {
        return `0 ~ ${currentZoneEnd}`
      }

      // 마지막 존
      if (isLastZone) {
        const previousZoneEnd = calculateZoneValue(idx - 1, max)
        return `${previousZoneEnd} ~ `
      }

      // 중간 존들
      const previousZoneEnd = calculateZoneValue(idx - 1, max)
      return `${previousZoneEnd} ~ ${currentZoneEnd}`
    },
    [index, individualRanges, type, calculateZoneValue],
  )

  useEffect(() => {
    if (ref.current) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].intersectionRatio > 0) {
            setIsVisible(true)
          }
        },
        { threshold: 0.5, root: document.querySelector(".ZonePlayerCont .MuiDataGrid-main") },
      )

      observer.observe(ref.current)

      return () => {
        observer.disconnect()
      }
    }
  }, [ref.current])

  const isDisabledFirstInput = useMemo(() => {
    return type !== "hr" || !(type === "hr" && index === 0) || !params.row.customMetricActive
  }, [type, index, params.row.customMetricActive])

  const getValueByMinMax = () => {
    const { customMetrics } = params.row
    if (!customMetrics.length) {
      return ""
    }

    if (index === 0 && type !== "hr" && type !== "deceleration") {
      return min !== 0 ? min.toFixed(fixedNumber) : min
    }
    if (index === 0 && type === "deceleration") {
      return 0
    }

    return customMetrics[newIndex - 1]?.value
  }

  /**
   * 칸 하나의 값을 바꾼다. cellIndex 는 **그 칸이 실제로 보여 주는 자리**여야 한다 —
   * 예전에는 왼쪽 칸이 모두 0(Zone 1)으로 들어가 있어, 다른 존을 고치다 포커스가 지나가기만 해도
   * blur 가 Zone 1 을 덮어썼다 (ERR-048). readOnly 인 칸은 아예 쓰지 않는다.
   */
  const handleInput = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    cellIndex: number,
    readOnly: boolean,
  ) => {
    // HR 은 정수만 — 소수점이 섞인 입력은 반영하지 않는다 (버그 #8)
    if (readOnly || cellIndex < 0 || !isAllowedZoneCellInput(type, e.target.value)) {
      return
    }

    const raw = e.target.value
    // checkZoneCellIsFloat 에서는 통과하지만 Number 변환 시 NaN 인 경우
    const nextValue =
      e.type === "blur"
        ? raw === "-" || raw === "." || raw === ""
          ? ""
          : Number(raw).toFixed(fixedNumber)
        : raw

    // 칸끼리 객체를 공유하지 않도록 바뀐 자리만 새로 만든다 — 직접 변형하면 다른 칸까지 따라 바뀐다
    setPlayerMetrics((prev) =>
      prev.map((player, i) => {
        if (i !== rowIndex || !player.customMetrics?.[cellIndex]) {
          return player
        }

        return {
          ...player,
          customMetrics: player.customMetrics.map((metric, j) =>
            j === cellIndex ? { ...metric, value: nextValue } : metric,
          ),
        }
      }),
    )
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // 방향키 이벤트가 DataGrid로 전파되지 않도록 막기
    if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) {
      e.stopPropagation()
    }
    // HR 은 소수점 키 자체를 막는다 (버그 #8)
    if (type === "hr" && HR_BLOCKED_KEYS.includes(e.key)) {
      e.preventDefault()
    }
  }

  const isExpanded = params.row.isExpanded ?? false

  return (
    <div className="zone-tab-table-cell" ref={ref}>
      {isVisible && (
        <>
          <span className="zone-tab-player-metric-range">
            {isNull(params.row.max) ? "-" : getPlayerValue(params.row.max, newIndex)}
          </span>
          {isExpanded && (
            <span className="zone-tab-custom-player-metric-input">
              <TextField
                value={getValueByMinMax() ?? ""}
                // hr 탭에서만 첫번째 셀이 작성 가능. blur 시 다른 셀과 같이 자릿수 반올림 (버그 #8)
                onChange={(e) => handleInput(e, newIndex - 1, isDisabledFirstInput)}
                onBlur={(e) => handleInput(e, newIndex - 1, isDisabledFirstInput)}
                onKeyDown={handleKeyDown}
                error={
                  !isDisabledFirstInput &&
                  checkIsErrCustomThreshold(
                    params.row.customMetrics[newIndex - 1]?.value ?? "",
                    newIndex - 1,
                    params.row.customMetrics?.map((metric: CustomPlayerMetric) => metric.value) ??
                      [],
                    params.row.customMetricActive,
                    isNegative,
                    step,
                    min,
                    max,
                  )
                }
                slotProps={{
                  input: {
                    readOnly: isDisabledFirstInput,
                  },
                }}
              />
              <span className="tilde-sign">~</span>
              {individualRanges?.[type].length && index !== individualRanges[type].length - 1 && (
                <TextField
                  value={params.row.customMetrics?.[newIndex]?.value ?? ""}
                  onChange={(e) => handleInput(e, newIndex, !params.row.customMetricActive)}
                  onBlur={(e) => handleInput(e, newIndex, !params.row.customMetricActive)}
                  onKeyDown={handleKeyDown}
                  error={checkIsErrCustomThreshold(
                    params.row.customMetrics[newIndex]?.value ?? "",
                    newIndex,
                    params.row.customMetrics?.map((metric: CustomPlayerMetric) => metric.value) ??
                      [],
                    params.row.customMetricActive,
                    isNegative,
                    step,
                    min,
                    max,
                  )}
                  slotProps={{
                    input: {
                      readOnly: !params.row.customMetricActive || false,
                    },
                  }}
                />
              )}
            </span>
          )}
        </>
      )}
    </div>
  )
}
